import { ArrowRight } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-36 lg:pt-40 pb-20 lg:pb-28 border-b border-line bg-white overflow-hidden"
    >
      {/* Faint ledger background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]">
        <div className="w-full h-full grid grid-cols-12">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="border-l border-ink" />
          ))}
        </div>
      </div>

      <div className="relative w-full max-w-none px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left column — unified headline group + CTAs */}
          <div className="lg:col-span-8">
            {/* Tight headline group */}
            <div className="animate-fade-up" style={{ animationDelay: "60ms" }}>
              <div className="flex items-center gap-3 mb-2">
                <span className="relative pulse-dot inline-block w-2 h-2 rounded-full bg-brand-orange" />
                <span
                  data-testid="hero-overline"
                  className="font-sans font-semibold text-xs sm:text-[13px] uppercase tracking-[0.32em] text-brand-orange leading-none"
                >
                  The Redirect
                </span>
              </div>

              <h1
                data-testid="hero-headline"
                className="font-display font-bold text-ink whitespace-nowrap tracking-[-0.02em] leading-[0.95] text-[clamp(2.25rem,8vw,5.5rem)] mt-1"
              >
                Scan. See. Shop.
              </h1>

              <p
                data-testid="hero-tagline"
                className="font-sans font-medium text-ink-soft mt-2 text-base sm:text-lg lg:text-xl leading-snug"
              >
                The verified women-owned registry.
              </p>
            </div>

            {/* Supporting body copy */}
            <p
              className="mt-8 max-w-2xl text-base sm:text-lg lg:text-xl leading-[1.55] text-ink-soft animate-fade-up"
              style={{ animationDelay: "220ms" }}
            >
              Empowering you to identify and support{" "}
              <span className="text-ink">
                women-owned brands and professional services
              </span>{" "}
              instantly at the point of sale or online — turning every purchase
              into progress.
            </p>

            {/* CTAs */}
            <div
              className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up"
              style={{ animationDelay: "320ms" }}
            >
              <a
                href="https://app.shescan.app"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-primary"
                onClick={() => {
                  try {
                    window.gtag?.("event", "select_content", {
                      content_type: "cta",
                      item_id: "hero_download_web_app",
                    });
                  } catch { /* noop */ }
                }}
                className="group inline-flex items-center justify-center gap-3 border-2 border-brand-orange bg-brand-orange text-white font-sans font-semibold uppercase text-sm tracking-widest px-10 py-5 hover:bg-ink hover:border-ink transition-colors duration-150"
              >
                Open the app
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://chromewebstore.google.com/detail/shescan-brand-finder/kiiinbbipichfgnnbncfpcfgaipcecom"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-secondary"
                onClick={() => {
                  try {
                    window.gtag?.("event", "select_content", {
                      content_type: "cta",
                      item_id: "hero_add_to_chrome",
                    });
                  } catch { /* noop */ }
                }}
                className="inline-flex items-center justify-center gap-3 border-2 border-line bg-white text-ink font-sans font-semibold uppercase text-sm tracking-widest px-10 py-5 hover:border-link transition-colors duration-150"
              >
                Add to Chrome
              </a>
            </div>
          </div>

          {/* Right column — lifestyle photo */}
          <aside
            className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-line animate-fade-up"
            style={{ animationDelay: "420ms" }}
            data-testid="hero-image-card"
          >
            <div className="flex items-center justify-between pb-3 border-b border-line">
              <span className="font-sans font-semibold text-xs uppercase tracking-widest text-link">
                In the wild
              </span>
              <span className="font-sans text-xs text-ink-soft tabular-nums">
                One scan · one decision
              </span>
            </div>

            <figure className="mt-6 relative border border-line overflow-hidden bg-white aspect-[1/2] sm:aspect-[9/16] lg:aspect-[1/2]">
              <img
                src="https://customer-assets.emergentagent.com/job_women-economy/artifacts/k6uxa9jf_Screenshot%202026-04-30%20at%2010.26.28%E2%80%AFAM.png"
                alt="A woman in a boutique scans a product with the SheScan app — a green checkmark appears on her phone."
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "100% center" }}
                loading="eager"
              />
              {/* Soft left-edge mask to clean any text bleed from the source image */}
              <div className="absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none" />
              {/* Bottom vignette for caption contrast */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/55 to-transparent pointer-events-none" />

              <figcaption className="absolute left-0 right-0 bottom-0 px-4 py-3 flex items-center justify-between">
                <span className="font-sans font-semibold text-[11px] uppercase tracking-widest text-white">
                  Verified · women-owned
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans font-semibold text-[11px] uppercase tracking-widest text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-orange pulse-dot" />
                  Live
                </span>
              </figcaption>
            </figure>

            <p className="mt-5 text-sm text-ink-soft leading-relaxed">
              Walk in. Scan. Decide. Or browse online and click. Every choice
              you make at the shelf or checkout becomes a vote for the economy
              you want.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}
